<template>
  <div class="flex flex-col p-6">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
      <div>
        <!-- Back navigation if filtering by opportunity -->
        <div v-if="$route.query.opportunityId" class="mb-3">
          <button
            @click="goBackToOpportunity"
            class="flex items-center text-primary-400 hover:text-primary-300 text-sm button-interactive"
          >
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
            </svg>
            Volver a la oportunidad
          </button>
        </div>
        
        <h1 class="text-2xl font-bold text-white">
          <span v-if="$route.query.opportunityId">Intenciones de la Oportunidad</span>
          <span v-else>Gestión de Intenciones</span>
        </h1>
        <p class="mt-1 text-sm text-content-300">
          <span v-if="$route.query.opportunityId">
            Intenciones de inversión para esta oportunidad específica
          </span>
          <span v-else>
            Administra las intenciones de inversión de los clientes
          </span>
        </p>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-gradient-to-r from-content-800 to-content-700 p-4 rounded-lg mb-6">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <label class="block text-sm font-medium text-white mb-2">Estado</label>
          <select
            v-model="filters.status"
            @change="applyFilters"
            class="border py-2 px-3 rounded-lg outline-none smooth focus:border-primary-500 smooth w-full bg-transparent"
          >
            <option value="">Todos los estados</option>
            <option value="pending">Pendientes</option>
            <option value="confirmed">Confirmadas</option>
            <option value="cancelled">Canceladas</option>
            <option value="closed">Cerradas</option>
          </select>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-white mb-2">Ordenar por</label>
          <select
            v-model="filters.sortBy"
            @change="applyFilters"
            class="border py-2 px-3 rounded-lg outline-none smooth focus:border-primary-500 smooth w-full bg-transparent"
          >
            <option value="newest">Más recientes</option>
            <option value="oldest">Más antiguos</option>
            <option value="amount_desc">Mayor monto</option>
            <option value="amount_asc">Menor monto</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-white mb-2">Items por página</label>
          <select
            v-model="pagination.limit"
            @change="applyFilters"
            class="border py-2 px-3 rounded-lg outline-none smooth focus:border-primary-500 smooth w-full bg-transparent"
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </select>
        </div>

        <div>
          <label class="block text-sm font-medium text-white mb-2">&nbsp;</label>
          <button
            @click="resetFilters"
            class="secondary-button w-full py-2"
          >
            Limpiar filtros
          </button>
        </div>
      </div>
      
      <!-- Show filter indicator if filtering by opportunity -->
      <div v-if="$route.query.opportunityId" class="mt-4 p-3 bg-primary-600 bg-opacity-20 border border-primary-500 rounded-lg">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <svg class="w-5 h-5 text-primary-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
            </svg>
            <span class="text-primary-300 text-sm">
              Filtrando por Oportunidad ID: {{ $route.query.opportunityId }}
            </span>
          </div>
          <button
            @click="clearOpportunityFilter"
            class="text-primary-400 hover:text-primary-300 text-sm button-interactive"
          >
            Mostrar todas las intenciones
          </button>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
      <div class="bg-gradient-to-r from-content-800 to-content-700 p-4 rounded-lg">
        <div class="text-2xl font-bold text-yellow-400">{{ stats.pending }}</div>
        <div class="text-sm text-content-300">Pendientes</div>
      </div>
      <div class="bg-gradient-to-r from-content-800 to-content-700 p-4 rounded-lg">
        <div class="text-2xl font-bold text-green-400">{{ stats.confirmed }}</div>
        <div class="text-sm text-content-300">Confirmadas</div>
      </div>
      <div class="bg-gradient-to-r from-content-800 to-content-700 p-4 rounded-lg">
        <div class="text-2xl font-bold text-red-400">{{ stats.cancelled }}</div>
        <div class="text-sm text-content-300">Canceladas</div>
      </div>
      <div class="bg-gradient-to-r from-content-800 to-content-700 p-4 rounded-lg">
        <div class="text-2xl font-bold text-blue-400">{{ stats.closed }}</div>
        <div class="text-sm text-content-300">Cerradas</div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <loader-icon class="h-10 w-10 text-primary-500"></loader-icon>
    </div>

    <!-- Intention Details Modal -->
    <div v-if="selectedIntention" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" @click="closeModal">
      <div class="bg-gradient-to-r from-content-800 to-content-700 rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" @click.stop>
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-xl font-bold text-white">Detalles de la Intención</h2>
          <button @click="closeModal" class="text-content-300 hover:text-white">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Client Information -->
        <div class="mb-6">
          <h3 class="text-lg font-semibold text-white mb-4">Información del Cliente</h3>
          <div class="bg-content-700 rounded-lg p-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-content-200 mb-1">Nombre Completo</label>
                <p class="text-white">{{ selectedIntention.client.name }} {{ selectedIntention.client.lastName }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-content-200 mb-1">Email</label>
                <p class="text-white">{{ selectedIntention.client.user?.email || '-' }}</p>
                <button 
                  v-if="selectedIntention.client.user?.email" 
                  @click="copyToClipboard(selectedIntention.client.user.email)"
                  class="text-primary-400 hover:text-primary-300 text-sm mt-1"
                >
                  Copiar email
                </button>
              </div>
              <div>
                <label class="block text-sm font-medium text-content-200 mb-1">Teléfono</label>
                <p class="text-white">{{ selectedIntention.client.phone || '-' }}</p>
                <button 
                  v-if="selectedIntention.client.phone" 
                  @click="copyToClipboard(selectedIntention.client.phone)"
                  class="text-primary-400 hover:text-primary-300 text-sm mt-1"
                >
                  Copiar teléfono
                </button>
              </div>
              <div>
                <label class="block text-sm font-medium text-content-200 mb-1">ID del Cliente</label>
                <p class="text-content-300">{{ selectedIntention.client.id }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Intention Information -->
        <div class="mb-6">
          <h3 class="text-lg font-semibold text-white mb-4">Detalles de la Intención</h3>
          <div class="bg-content-700 rounded-lg p-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-content-200 mb-1">Monto de Inversión</label>
                <p class="text-2xl font-bold text-green-400">{{ formatCurrency(selectedIntention.amount) }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-content-200 mb-1">Rendimiento Estimado</label>
                <p class="text-xl font-bold text-purple-400">{{ formatCurrency(calculateReturn(selectedIntention)) }}</p>
                <p class="text-xs text-content-300 mt-1">Total: {{ formatCurrency(selectedIntention.amount + calculateReturn(selectedIntention)) }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-content-200 mb-1">Estado</label>
                <span
                  class="inline-flex px-3 py-1 text-sm font-semibold rounded-full"
                  :class="getStatusBadgeClass(selectedIntention.status)"
                >
                  {{ getStatusLabel(selectedIntention.status) }}
                </span>
              </div>
              <div>
                <label class="block text-sm font-medium text-content-200 mb-1">Fecha de Intención</label>
                <p class="text-white">{{ formatDate(selectedIntention.intentionDate) }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-content-200 mb-1">ID de Intención</label>
                <p class="text-content-300">{{ selectedIntention.id }}</p>
              </div>
            </div>
            <div v-if="selectedIntention.notes" class="mt-4">
              <label class="block text-sm font-medium text-content-200 mb-1">Notas</label>
              <p class="text-white bg-content-600 p-3 rounded">{{ selectedIntention.notes }}</p>
            </div>
          </div>
        </div>

        <!-- Opportunity Information -->
        <div class="mb-6">
          <h3 class="text-lg font-semibold text-white mb-4">Información de la Oportunidad</h3>
          <div class="bg-content-700 rounded-lg p-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-content-200 mb-1">Título</label>
                <p class="text-white">{{ selectedIntention.opportunity.title }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-content-200 mb-1">Tipos de Inversión Disponibles</label>
                <div class="text-white">
                  <span v-if="selectedIntention.opportunity.enableDebt" class="inline-block mr-2">
                    <span class="text-green-400">Deuda:</span> {{ selectedIntention.opportunity.annualFixedRate }}%
                  </span>
                  <span v-if="selectedIntention.opportunity.enableCoOwnership" class="inline-block">
                    <span class="text-blue-400">Adquisición de inmueble:</span> {{ selectedIntention.opportunity.estimatedIRR }}% TIR
                  </span>
                  <span v-if="!selectedIntention.opportunity.enableDebt && !selectedIntention.opportunity.enableCoOwnership" class="text-content-400">
                    No especificado
                  </span>
                </div>
              </div>
              <div v-if="selectedIntention.investmentType">
                <label class="block text-sm font-medium text-content-200 mb-1">Tipo Elegido por el Cliente</label>
                <div class="text-white">
                  <span
                    v-if="selectedIntention.investmentType === 'debt'"
                    class="inline-flex items-center px-3 py-1 bg-green-900 bg-opacity-30 border border-green-500 rounded-full"
                  >
                    <span class="text-green-400 font-medium">Deuda</span>
                    <span class="text-green-300 ml-2">{{ selectedIntention.opportunity.annualFixedRate }}% anual</span>
                  </span>
                  <span
                    v-else-if="selectedIntention.investmentType === 'co-ownership'"
                    class="inline-flex items-center px-3 py-1 bg-blue-900 bg-opacity-30 border border-blue-500 rounded-full"
                  >
                    <span class="text-blue-400 font-medium">Adquisición de inmueble</span>
                    <span class="text-blue-300 ml-2">{{ selectedIntention.opportunity.estimatedIRR }}% TIR</span>
                  </span>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-content-200 mb-1">Plazo</label>
                <p class="text-white">{{ selectedIntention.opportunity.term }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-content-200 mb-1">Estado</label>
                <p class="text-white">{{ getStatusLabel(selectedIntention.opportunity.status) }}</p>
              </div>
            </div>
            <div class="mt-4 text-center">
              <button
                @click="goToOpportunity"
                class="secondary-button px-4 py-2"
              >
                Ver Oportunidad Completa
              </button>
            </div>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="flex gap-4 justify-center">
          <button
            v-if="selectedIntention.status === 'pending'"
            @click="updateStatus(selectedIntention, 'confirmed')"
            class="secondary-button px-4 py-2"
          >
            Confirmar Intención
          </button>
          <button
            v-if="selectedIntention.status === 'confirmed'"
            @click="closeIntention(selectedIntention)"
            class="secondary-button px-4 py-2"
          >
            Cerrar Operación
          </button>
          <button
            v-if="['pending', 'confirmed'].includes(selectedIntention.status)"
            @click="updateStatus(selectedIntention, 'cancelled')"
            class="secondary-button px-4 py-2"
          >
            Cancelar Intención
          </button>
          <button
            @click="closeModal"
            class="secondary-button px-4 py-2"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>

    <!-- Intentions Table -->
    <table v-else class="w-full table-auto text-sm rounded-lg shadow-primary mt-6">
          <thead class="bg-content-700">
            <tr>
              <th class="px-3 sm:px-6 py-3 text-left text-xs font-medium text-content-200 uppercase tracking-wider">
                Cliente
              </th>
              <th class="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-content-200 uppercase tracking-wider">
                Oportunidad
              </th>
              <th class="px-3 sm:px-6 py-3 text-left text-xs font-medium text-content-200 uppercase tracking-wider">
                Monto
              </th>
              <th class="px-3 sm:px-6 py-3 text-left text-xs font-medium text-content-200 uppercase tracking-wider">
                Estado
              </th>
              <th class="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-content-200 uppercase tracking-wider">
                Fecha
              </th>
              <th class="hidden sm:table-cell px-6 py-3 text-center text-xs font-medium text-content-200 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody class="text-white">
            <tr 
              v-for="intention in intentions" 
              :key="intention.id"
              class="cursor-pointer hover:bg-content-600 sm:cursor-default"
              @click="viewDetails(intention)"
              :data-intention-id="intention.id"
            >
              <!-- Client Info -->
              <td class="px-3 sm:px-6 py-4">
                <div>
                  <div class="text-sm font-medium text-white">
                    {{ intention.client.name }} {{ intention.client.lastName }}
                  </div>
                  <div class="text-xs sm:text-sm text-content-300">
                    {{ intention.client.user?.email || '-' }}
                  </div>
                  <!-- Mobile-only info -->
                  <div class="sm:hidden mt-1 text-xs text-content-300 truncate">
                    {{ intention.opportunity.title }}
                  </div>
                </div>
              </td>

              <!-- Opportunity Info -->
              <td class="hidden sm:table-cell px-6 py-4 whitespace-nowrap">
                <div>
                  <div class="text-sm font-medium text-white">
                    {{ intention.opportunity.title }}
                  </div>
                  <div class="text-sm text-content-300">
                    {{ getInstrumentLabel(intention.opportunity.instrument) }} - {{ intention.opportunity.annualFixedRate }}%
                  </div>
                </div>
              </td>

              <!-- Amount -->
              <td class="px-3 sm:px-6 py-4 whitespace-nowrap">
                <div class="text-sm font-medium text-white">
                  {{ formatCurrency(intention.amount) }}
                </div>
              </td>

              <!-- Status -->
              <td class="px-3 sm:px-6 py-4 whitespace-nowrap">
                <span 
                  class="inline-flex px-2 py-1 text-xs font-semibold rounded-full"
                  :class="getStatusBadgeClass(intention.status)"
                >
                  {{ getStatusLabel(intention.status) }}
                </span>
              </td>

              <!-- Date -->
              <td class="hidden md:table-cell px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-content-300">
                  {{ formatDate(intention.intentionDate) }}
                </div>
              </td>

              <!-- Actions -->
              <td class="hidden sm:table-cell px-6 py-4 whitespace-nowrap text-center"
                  @click.stop>
                <div class="flex justify-center gap-2">
                  <!-- Confirm button -->
                  <button
                    v-if="intention.status === 'pending'"
                    @click="updateStatus(intention, 'confirmed')"
                    class="secondary-button"
                    title="Confirmar intención"
                  >
                    Confirmar
                  </button>

                  <!-- Close button -->
                  <button
                    v-if="intention.status === 'confirmed'"
                    @click="closeIntention(intention)"
                    class="secondary-button"
                    title="Cerrar operación"
                  >
                    Cerrar
                  </button>

                  <!-- Cancel button -->
                  <button
                    v-if="['pending', 'confirmed'].includes(intention.status)"
                    @click="updateStatus(intention, 'cancelled')"
                    class="secondary-button"
                    title="Cancelar intención"
                  >
                    Cancelar
                  </button>

                  <!-- View details button -->
                  <button
                    @click="viewDetails(intention)"
                    class="secondary-button"
                    title="Ver detalles"
                  >
                    Ver
                  </button>
                </div>
              </td>
            </tr>

            <!-- Empty state -->
            <tr v-if="intentions.length === 0">
              <td colspan="6" class="px-6 py-12 text-center">
                <div class="text-content-400">
                  <svg class="mx-auto h-12 w-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div class="text-sm text-content-300">No hay intenciones de inversión</div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="mt-6 flex justify-between items-center">
      <div class="text-sm text-content-300">
        Mostrando {{ ((currentPage - 1) * pagination.limit) + 1 }} - 
        {{ Math.min(currentPage * pagination.limit, totalItems) }} de {{ totalItems }} intenciones
      </div>
      
      <div class="flex gap-2">
        <button
          :disabled="currentPage <= 1"
          @click="changePage(currentPage - 1)"
          class="secondary-button disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Anterior
        </button>
        
        <span class="px-3 py-2 text-sm text-white bg-primary-600 rounded">
          {{ currentPage }} de {{ totalPages }}
        </span>
        
        <button
          :disabled="currentPage >= totalPages"
          @click="changePage(currentPage + 1)"
          class="secondary-button disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Siguiente
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../../../api";

export default {
  name: "IntentionsIndex",
  data() {
    return {
      intentions: [],
      loading: false,
      selectedIntention: null,
      filters: {
        status: '',
        sortBy: 'newest'
      },
      pagination: {
        limit: 20
      },
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      stats: {
        pending: 0,
        confirmed: 0,
        cancelled: 0,
        closed: 0
      }
    };
  },
  created() {
    this.$store.commit("setSelectedTab", "adminIntentions");
    
    // Check if we have an opportunityId filter from URL
    if (this.$route.query.opportunityId) {
      this.filters.opportunityId = this.$route.query.opportunityId;
    }
    
    // Check if we have clientId filter from URL
    if (this.$route.query.clientId) {
      this.filters.clientId = this.$route.query.clientId;
    }
    
    this.loadIntentions();
    this.loadStats();
  },
  mounted() {
    // Handle highlight parameter for specific intention
    if (this.$route.query.highlight) {
      // Wait for intentions to load then highlight the specific one
      this.$nextTick(() => {
        setTimeout(() => {
          this.highlightIntention(this.$route.query.highlight);
        }, 500);
      });
    }
  },
  methods: {
    async loadIntentions() {
      this.loading = true;
      
      try {
        const params = {
          page: this.currentPage,
          limit: this.pagination.limit,
          ...this.filters
        };

        const response = await api.opportunities.getAllIntentions(params);
        
        this.intentions = response.data;
        this.currentPage = response.pagination.currentPage;
        this.totalPages = response.pagination.totalPages;
        this.totalItems = response.pagination.totalItems;
      } catch (error) {
        console.error('Error loading intentions:', error);
        this.$store.dispatch("errorSnack", "Error al cargar las intenciones");
      }

      this.loading = false;
    },

    async loadStats() {
      try {
        // Load all intentions to calculate stats
        const response = await api.opportunities.getAllIntentions({ limit: 1000 });
        const allIntentions = response.data;

        this.stats = {
          pending: allIntentions.filter(i => i.status === 'pending').length,
          confirmed: allIntentions.filter(i => i.status === 'confirmed').length,
          cancelled: allIntentions.filter(i => i.status === 'cancelled').length,
          closed: allIntentions.filter(i => i.status === 'closed').length
        };
      } catch (error) {
        console.error('Error loading stats:', error);
      }
    },

    async updateStatus(intention, newStatus) {
      try {
        const response = await api.opportunities.updateIntentionStatus(intention.id, newStatus);
        
        this.$store.dispatch("successSnack", response.message);
        await this.loadIntentions();
        await this.loadStats();
        this.closeModal();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "Error al actualizar el estado");
      }
    },

    async closeIntention(intention) {
      try {
        const response = await api.opportunities.closeIntention(intention.id);
        
        this.$store.dispatch("successSnack", response.message);
        await this.loadIntentions();
        await this.loadStats();
        this.closeModal();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message || "Error al cerrar la operación");
      }
    },

    viewDetails(intention) {
      this.selectedIntention = intention;
    },

    closeModal() {
      this.selectedIntention = null;
    },

    goToOpportunity() {
      this.$router.push({ 
        name: 'adminOpportunityDetail', 
        params: { id: this.selectedIntention.opportunity.id } 
      });
    },

    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$store.dispatch("successSnack", "Copiado al portapapeles");
      } catch (error) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        this.$store.dispatch("successSnack", "Copiado al portapapeles");
      }
    },

    applyFilters() {
      this.currentPage = 1;
      this.loadIntentions();
    },

    resetFilters() {
      this.filters = {
        status: '',
        sortBy: 'newest'
      };
      this.pagination.limit = 20;
      this.currentPage = 1;
      this.loadIntentions();
    },

    changePage(page) {
      this.currentPage = page;
      this.loadIntentions();
    },

    getStatusBadgeClass(status) {
      const classes = {
        pending: 'bg-yellow-100 text-yellow-800',
        confirmed: 'bg-green-100 text-green-800',
        cancelled: 'bg-red-100 text-red-800',
        closed: 'bg-blue-100 text-blue-800'
      };
      return classes[status] || 'bg-gray-100 text-gray-800';
    },

    getStatusLabel(status) {
      const labels = {
        pending: 'Pendiente',
        confirmed: 'Confirmada',
        cancelled: 'Cancelada',
        closed: 'Cerrada'
      };
      return labels[status] || status;
    },

    formatCurrency(amount) {
      const numericAmount = parseFloat(amount) || 0;
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(numericAmount);
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    goBackToOpportunity() {
      const opportunityId = this.$route.query.opportunityId;
      if (opportunityId) {
        this.$router.push({ 
          name: 'adminOpportunityDetail', 
          params: { id: opportunityId }
        });
      } else {
        this.$router.push({ name: 'adminOpportunitiesIndex' });
      }
    },
    
    clearOpportunityFilter() {
      this.$router.push({ name: 'adminIntentionsIndex' });
    },

    getInstrumentLabel(instrument) {
      const labels = {
        'Debt': 'Deuda',
        'Co-ownership': 'Adquisición de inmueble'
      };
      return labels[instrument] || instrument;
    },
    
    highlightIntention(intentionId) {
      const intention = this.intentions.find(i => i.id == intentionId);
      if (intention) {
        // Automatically open the details modal for the highlighted intention
        this.selectedIntention = intention;

        // Find the table row and add a temporary highlight effect
        this.$nextTick(() => {
          const tableRow = document.querySelector(`tr[data-intention-id="${intentionId}"]`);
          if (tableRow) {
            tableRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
            tableRow.classList.add('bg-primary-600', 'bg-opacity-20', 'border', 'border-primary-500');

            // Remove highlight after 3 seconds
            setTimeout(() => {
              tableRow.classList.remove('bg-primary-600', 'bg-opacity-20', 'border', 'border-primary-500');
            }, 3000);
          }
        });
      }
    },

    calculateReturn(intention) {
      if (!intention || !intention.amount || !intention.opportunity) {
        return 0;
      }

      const amount = parseFloat(intention.amount);
      let rate = 0;

      // Determine which rate to use based on investment type
      if (intention.investmentType === 'debt' && intention.opportunity.annualFixedRate) {
        rate = parseFloat(intention.opportunity.annualFixedRate);
      } else if (intention.investmentType === 'co-ownership' && intention.opportunity.estimatedIRR) {
        rate = parseFloat(intention.opportunity.estimatedIRR);
      } else if (intention.opportunity.annualFixedRate) {
        // Fallback to debt rate if no type specified
        rate = parseFloat(intention.opportunity.annualFixedRate);
      } else if (intention.opportunity.estimatedIRR) {
        // Fallback to IRR
        rate = parseFloat(intention.opportunity.estimatedIRR);
      }

      if (rate === 0) return 0;

      // Extract months from term
      const term = intention.opportunity.term || '12 meses';
      const monthsMatch = term.match(/(\d+)/);
      const months = monthsMatch ? parseInt(monthsMatch[1]) : 12;

      // Calculate return
      const annualReturn = amount * (rate / 100);
      const projectedReturn = (annualReturn * months) / 12;

      return projectedReturn;
    }
  }
};
</script>